import { useState } from "react";
import { Download, Smartphone, Share2, MoreVertical, Plus, CheckCircle2, ExternalLink, QrCode, Copy, AlertTriangle, Maximize, Lock, Power } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

export function PWAInstructions() {
  const [copied, setCopied] = useState(false);
  const garcomUrl = `${window.location.origin}/dashboard/garcom`;

  const copyLink = () => {
    navigator.clipboard.writeText(garcomUrl);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">App Garçom Mobile</CardTitle>
              <CardDescription>
                Instale o app no celular dos garçons para acesso rápido
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Acesso rápido
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Notificações
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Tela cheia
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* QR Code Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Acesso Rápido
          </CardTitle>
          <CardDescription>
            Escaneie o QR Code no celular do garçom para abrir a página
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col items-center gap-4 p-4 bg-white rounded-lg border">
            <QRCodeSVG
              value={garcomUrl}
              size={180}
              level="H"
              includeMargin
            />
            <p className="text-xs text-muted-foreground text-center">
              Escaneie com a câmera do celular
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 p-2 bg-muted rounded text-sm font-mono truncate">
              {garcomUrl}
            </div>
            <Button variant="outline" size="icon" onClick={copyLink}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" asChild>
              <a href={garcomUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Android Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.29-.53-2.72-.84-4.29-.84-1.62 0-3.1.32-4.43.87L5.83 5.67c-.18-.29-.54-.38-.83-.22-.3.16-.42.54-.26.85L6.6 9.48C4.16 10.88 2.5 13.2 2.5 15.92h19c0-2.72-1.66-5.04-4.09-6.44zM7 13.5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
            </svg>
            Instalação no Android
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Abra o link no Chrome</p>
                <p className="text-sm text-muted-foreground">
                  Use o navegador Chrome para melhor compatibilidade
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                2
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Toque no menu
                  <span className="inline-flex items-center justify-center h-6 w-6 bg-muted rounded">
                    <MoreVertical className="h-4 w-4" />
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Ícone de três pontos no canto superior direito
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                3
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Selecione "Adicionar à tela inicial"
                  <Download className="h-4 w-4 text-muted-foreground" />
                </p>
                <p className="text-sm text-muted-foreground">
                  Ou "Instalar aplicativo" se disponível
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/10 text-success flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </span>
              <div>
                <p className="font-medium">Pronto!</p>
                <p className="text-sm text-muted-foreground">
                  O app aparecerá na tela inicial do celular
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* iOS Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Instalação no iPhone/iPad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Abra o link no Safari</p>
                <p className="text-sm text-muted-foreground">
                  O Safari é necessário para instalar PWAs no iOS
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                2
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Toque no botão compartilhar
                  <span className="inline-flex items-center justify-center h-6 w-6 bg-muted rounded">
                    <Share2 className="h-4 w-4" />
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Ícone na parte inferior da tela
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                3
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Selecione "Adicionar à Tela Inicial"
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </p>
                <p className="text-sm text-muted-foreground">
                  Role para baixo se necessário para encontrar a opção
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                4
              </span>
              <div>
                <p className="font-medium">Toque em "Adicionar"</p>
                <p className="text-sm text-muted-foreground">
                  No canto superior direito
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/10 text-success flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </span>
              <div>
                <p className="font-medium">Pronto!</p>
                <p className="text-sm text-muted-foreground">
                  O app aparecerá na tela inicial do iPhone
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Usage Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Como usar o App</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">1</span>
              </div>
              <div>
                <p className="font-medium text-sm">Selecionar Mesa</p>
                <p className="text-xs text-muted-foreground">
                  Toque na mesa que deseja atender
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">2</span>
              </div>
              <div>
                <p className="font-medium text-sm">Adicionar Produtos</p>
                <p className="text-xs text-muted-foreground">
                  Busque ou navegue pelas categorias e toque para adicionar
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">3</span>
              </div>
              <div>
                <p className="font-medium text-sm">Observações</p>
                <p className="text-xs text-muted-foreground">
                  Toque em "Obs" para adicionar observações ao item
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-success">4</span>
              </div>
              <div>
                <p className="font-medium text-sm">Enviar Pedido</p>
                <p className="text-xs text-muted-foreground">
                  Revise o pedido e toque em "Enviar Pedido"
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alternative for problematic devices */}
      <Alert className="border-amber-500/50 bg-amber-500/10">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertTitle className="text-amber-600">Opção não aparece?</AlertTitle>
        <AlertDescription className="text-sm text-muted-foreground">
          Se a opção "Adicionar à tela inicial" não aparecer no menu:
          <ol className="mt-2 ml-4 list-decimal space-y-1">
            <li>Desative "Site para computador" no menu do Chrome</li>
            <li>Recarregue a página</li>
            <li>Para dispositivos dedicados, considere usar o <strong>Fully Kiosk Browser</strong></li>
          </ol>
        </AlertDescription>
      </Alert>
    </div>
  );
}
